import {
  Flex,
  Loader,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { ChatSession } from "../models/ChatSession";
import React, { FC, useRef } from "react";
import { ChatSessionValues } from "../hooks/useChatSessions";
import { UserStatus } from "../models/Message";
import ChatSessionItem from "./ChatSessionItem";

interface SessionTabPanelProps {
  tabValue: string;
  chatSessionValues: ChatSessionValues;
  sessionUserStatuses: Map<string, UserStatus>;
  unreadCounts: Map<string, number>;
  lastMessages: Map<string, string>;
}

const SessionTabPanel: FC<SessionTabPanelProps> = ({
  tabValue,
  chatSessionValues,
  sessionUserStatuses,
  unreadCounts,
  lastMessages,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const hasFiredRef = useRef(false);

  const theme = useMantineTheme();

  const isAssignedPanel = tabValue === "assigned";

  const handleScrollPositionChange = ({ x, y }: { x: number; y: number }) => {
    const el = viewportRef.current;
    if (!el) return;

    const scrollBottom = el.scrollHeight - (y + el.clientHeight);

    const threshold = 200;

    if (scrollBottom <= threshold) {
      if (!hasFiredRef.current) {
        hasFiredRef.current = true;
        chatSessionValues.incrementPageNum();
      }
    } else {
      // Reset the flag when you scroll up out of the threshold
      hasFiredRef.current = false;
    }
  };

  return (
    <Tabs.Panel
      h="100%"
      w="100%"
      value={tabValue}
      pt="md"
      className="chat-panel-container"
    >
      <ScrollArea
        overscrollBehavior="contain"
        viewportRef={viewportRef}
        type="auto"
        w="100%"
        h="100%"
        onScrollPositionChange={handleScrollPositionChange}
      >
        <Stack h="100%" w="100%">
          {chatSessionValues.sessionsLoading ? (
            <Flex h="75%" justify="center" align="center">
              <Loader />
            </Flex>
          ) : (
            <>
              {chatSessionValues.chatSessions?.map(
                (
                  chatSession: ChatSession,
                  index: number,
                ): React.JSX.Element => {
                  const isSelected =
                    chatSession.sessionId ===
                    chatSessionValues.activeSession?.sessionId;
                  const shadow = isSelected ? "xs" : "lg";
                  const background = isSelected
                    ? theme.colors.gray[4]
                    : "white";
                  const key = isAssignedPanel
                    ? `session-button-${index}`
                    : `unassigned-session-button-${index}`;

                  return (
                    <ChatSessionItem
                      key={key}
                      sessionKey={key}
                      shadow={shadow}
                      background={background}
                      isAssignedPanel={isAssignedPanel}
                      chatSessionValues={chatSessionValues}
                      chatSession={chatSession}
                      unreadCounts={unreadCounts}
                      lastMessages={lastMessages}
                      sessionUserStatuses={sessionUserStatuses}
                    />
                  );
                },
              )}
              {chatSessionValues.chatSessions?.length === 0 && (
                <Flex h="75%" justify="center" align="center">
                  <Text size="lg">
                    {isAssignedPanel
                      ? "You have no assigned chat sessions."
                      : "There are no unassigned chat sessions."}
                  </Text>
                </Flex>
              )}
            </>
          )}
        </Stack>
      </ScrollArea>
    </Tabs.Panel>
  );
};

export default SessionTabPanel;
